import type { CSSProperties } from 'react';

interface EmailHtmlPreviewProps {
  /**
   * Raw e-mail-template markup to display. The content is treated as
   * **untrusted** — admins can author arbitrary HTML, so it must never be
   * injected via `dangerouslySetInnerHTML`.
   */
  html: string;
  /** Accessible title for the underlying iframe (required for a11y). */
  title: string;
  className?: string;
  style?: CSSProperties;
}

/**
 * Safely renders untrusted e-mail-template HTML.
 *
 * E-mail templates are admin-editable and may contain arbitrary markup, which
 * makes rendering them via `dangerouslySetInnerHTML` a stored-XSS vector. This
 * component isolates the markup inside a sandboxed `<iframe>`: because the
 * sandbox omits `allow-scripts`, no embedded JavaScript can execute (script
 * tags, inline event handlers and `javascript:` URLs are all neutralised),
 * while the HTML/CSS layout still renders faithfully.
 *
 * It is the single source of truth for previewing template HTML — always use
 * this component instead of injecting template markup directly into the DOM.
 */
export function EmailHtmlPreview({ html, title, className, style }: EmailHtmlPreviewProps) {
  return (
    <iframe
      srcDoc={html}
      // Security: the sandbox intentionally omits `allow-scripts`, so the
      // framed template cannot execute any JavaScript (XSS-safe).
      sandbox="allow-same-origin"
      title={title}
      className={className}
      style={style}
    />
  );
}
